import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { showAlert } from 'actions/app';
import { useDailyPlanRoles } from 'features/daily-plan/hooks';
import { useUserContext } from 'providers/UserProvider';
import API from 'services/API';
import { TEAM_FILTERS_STORAGE_KEYS } from 'UI/pages/DailyPlan/DailyPlan.constants';
import { getErrorMessage } from 'UI/utils';

import { TEAM_ENDPOINTS } from '../TeamSelectors/TeamSelectors.constants';
import { getDefaultRecruiterOption } from '../utils';

export const useTeamFilters = (onFilterChange, initialFilters = {}) => {
  const [currentUser] = useUserContext();
  const dispatch = useDispatch();

  const {
    isAccountability,
    isDirector,
    isOperations,
    shouldShowTeamSelector,
    shouldShowRecruiterSelector
  } = useDailyPlanRoles();

  const [availableCoaches, setAvailableCoaches] = useState([]);

  const [selectedCoach, setSelectedCoach] = useState(() => {
    if (!shouldShowTeamSelector) return null;
    return initialFilters.coach?.value || null;
  });

  const [selectedRecruiter, setSelectedRecruiter] = useState(() => {
    if (!shouldShowRecruiterSelector) return null;
    return initialFilters.recruiter?.value || null;
  });

  const defaultRecruiterOption = useMemo(
    () => getDefaultRecruiterOption({ currentUser }),
    [currentUser]
  );

  const recruiterPrependOptions = useMemo(
    () => (defaultRecruiterOption ? [defaultRecruiterOption] : []),
    [defaultRecruiterOption]
  );

  const externalCoachValue = initialFilters.coach?.value;

  const externalRecruiterValue = initialFilters.recruiter?.value;
  const selectedCoachId = selectedCoach?.id;

  const coachId = currentUser?.teamRelation?.coachId;

  const recruiterUrl = useMemo(() => {
    if (!shouldShowRecruiterSelector) return null;

    if (isAccountability || isOperations) {
      return selectedCoachId ? TEAM_ENDPOINTS.recruitersByCoach(selectedCoachId) : null;
    }

    if (isDirector && coachId) {
      return TEAM_ENDPOINTS.recruitersByCoach(coachId);
    }

    return null;
  }, [
    shouldShowRecruiterSelector,
    isAccountability,
    isDirector,
    isOperations,
    selectedCoachId,
    coachId
  ]);

  useEffect(() => {
    if (shouldShowTeamSelector && availableCoaches.length === 0) {
      const fetchCoaches = async () => {
        try {
          const response = await API.get(TEAM_ENDPOINTS.coaches);

          const coaches = response.data?.data || response.data || [];
          setAvailableCoaches(coaches);

          if (!selectedCoach && coaches.length > 0) {
            const firstCoach = coaches[0];
            setSelectedCoach(firstCoach);
            localStorage.setItem(
              TEAM_FILTERS_STORAGE_KEYS.DAILY_PLAN_COACH,
              JSON.stringify(firstCoach)
            );
          }
        } catch (error) {
          dispatch(
            showAlert({
              severity: 'error',
              title: 'Error',
              body: getErrorMessage(error)
            })
          );
        }
      };

      fetchCoaches();
    }
  }, [shouldShowTeamSelector, availableCoaches.length, selectedCoach, dispatch]);

  useEffect(() => {
    setSelectedCoach(externalCoachValue || null);
    setSelectedRecruiter(externalRecruiterValue || null);
  }, [externalCoachValue, externalRecruiterValue]);

  const handleCoachSelect = useCallback(
    (event, newValue) => {
      setSelectedCoach(newValue);
      setSelectedRecruiter(null);

      localStorage.setItem(TEAM_FILTERS_STORAGE_KEYS.DAILY_PLAN_COACH, JSON.stringify(newValue));
      localStorage.removeItem(TEAM_FILTERS_STORAGE_KEYS.DAILY_PLAN_RECRUITER);

      const updatedFilters = {
        coach: {
          paramName: 'coachIds',
          value: newValue
        }
      };

      if (onFilterChange) {
        onFilterChange(updatedFilters);
      }
    },
    [onFilterChange]
  );

  const handleRecruiterSelect = useCallback(
    (event, newValue) => {
      setSelectedRecruiter(newValue);

      localStorage.setItem(
        TEAM_FILTERS_STORAGE_KEYS.DAILY_PLAN_RECRUITER,
        JSON.stringify(newValue)
      );

      const updatedFilters = {
        ...(selectedCoach && {
          coach: {
            paramName: 'coachIds',
            value: selectedCoach
          }
        }),
        recruiter: {
          paramName: 'assignedTo',
          value: newValue
        }
      };

      if (onFilterChange) {
        onFilterChange(updatedFilters);
      }
    },
    [selectedCoach, onFilterChange]
  );

  return {
    selectedCoach,
    selectedRecruiter,
    shouldShowTeamSelector,
    shouldShowRecruiterSelector,
    availableCoaches,
    recruiterUrl,
    defaultRecruiterOption,
    recruiterPrependOptions,
    handleCoachSelect,
    handleRecruiterSelect
  };
};
